import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const WARN_MS = 15 * 60 * 1000; // warn when < 15 min remain

const readExp = () => {
  const v = localStorage.getItem("vessel_session_exp");
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export default function SessionNotice() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(null);

  const tick = useCallback(() => {
    const exp = readExp();
    if (!exp) { setRemaining(null); return; }
    setRemaining(exp - Date.now());
  }, []);

  useEffect(() => {
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [tick]);

  useEffect(() => {
    if (remaining !== null && remaining <= 0) {
      signOut();
      navigate("/admin/login", { replace: true });
    }
  }, [remaining, signOut, navigate]);

  const reAuth = () => { signOut(); navigate("/admin/login", { replace: true }); };

  const show = remaining !== null && remaining > 0 && remaining <= WARN_MS;
  const mins = show ? Math.max(1, Math.ceil(remaining / 60000)) : 0;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="session-notice"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden border-b border-[var(--vsl-limestone)]/40 bg-[var(--vsl-limestone)]/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
            <p className="font-mono text-[11px] tracking-wide text-[var(--vsl-limestone)] inline-flex items-center gap-2">
              <AlertTriangle size={13} />
              Your session expires in {mins} minute{mins > 1 ? "s" : ""}. Save your work and sign in again to continue.
            </p>
            <button data-testid="session-reauth-btn" onClick={reAuth}
              className="shrink-0 font-mono text-[10px] tracking-[0.2em] uppercase border border-[var(--vsl-limestone)]/60 text-[var(--vsl-limestone)] px-3 py-1.5 hover:bg-[var(--vsl-limestone)] hover:text-[var(--vsl-bg)] transition-colors">
              Sign in again
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
